import type { StepNode } from '@segmently/schema-models/v2';

const TOP_LEVEL_KEYS_TO_KEEP = new Set([
  'id',
  'entityId',
  'name',
  'screenType',
  'position',
  'order',
  'tags',
  'createdAt',
  'updatedAt',
  'aiContent',
  'aiMeta',
  'aiAssets',
]);

const SEMANTIC_OR_ROUTING_KEYS = new Set([
  'action',
  'actions',
  'assetId',
  'assetUrl',
  'condition',
  'conditions',
  'description',
  'fallbackUrl',
  'imageUrl',
  'label',
  'labels',
  'locale',
  'locales',
  'media',
  'mediaUrl',
  'nextScreenId',
  'productId',
  'productIds',
  'routeGuard',
  'targetScreenId',
  'text',
  'texts',
  'title',
  'translations',
  'transition',
  'transitions',
  'url',
  'variable',
  'variableId',
  'variableName',
  'variableValue',
  'videoUrl',
]);

function cloneJson<T>(value: T): T {
  return value === undefined ? value : JSON.parse(JSON.stringify(value)) as T;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function shouldPreserveKey(key: string): boolean {
  return SEMANTIC_OR_ROUTING_KEYS.has(key) || SEMANTIC_OR_ROUTING_KEYS.has(key.toLowerCase());
}

function restoreSemanticAndRoutingValues(source: unknown, target: unknown): unknown {
  if (Array.isArray(source) && Array.isArray(target)) {
    return target.map((targetItem, index) => restoreSemanticAndRoutingValues(source[index], targetItem));
  }

  if (!isObject(source) || !isObject(target)) {
    return target;
  }

  const restored: Record<string, unknown> = { ...target };
  for (const [key, sourceValue] of Object.entries(source)) {
    if (sourceValue === undefined) continue;

    if (shouldPreserveKey(key)) {
      restored[key] = cloneJson(sourceValue);
      continue;
    }

    const targetValue = restored[key];
    if (
      (isObject(sourceValue) && isObject(targetValue))
      || (Array.isArray(sourceValue) && Array.isArray(targetValue))
    ) {
      restored[key] = restoreSemanticAndRoutingValues(sourceValue, targetValue);
    }
  }

  return restored;
}

/**
 * Restores content and routing values that should not be changed by a visual
 * theme remap. The theme engine is allowed to replace structural header/footer
 * regions; this pass keeps the user's generated copy, variable bindings, media,
 * products, and navigation decisions intact.
 */
export function preserveThemeApplyScreenContent<T extends StepNode>(
  before: T,
  themedAfter: T,
): T {
  const preserved = cloneJson(themedAfter) as Record<string, unknown>;
  const source = before as unknown as Record<string, unknown>;

  for (const key of TOP_LEVEL_KEYS_TO_KEEP) {
    if (source[key] !== undefined) {
      preserved[key] = cloneJson(source[key]);
    }
  }

  preserved.content = restoreSemanticAndRoutingValues(source.content, preserved.content);
  return preserved as T;
}
